<template>
  <ResponsiveDialog
    v-model="visible"
    :title="title"
    width="440px"
    :close-on-click-modal="false"
  >
    <div class="esd" :class="`esd--${mode}`">
      <!--
        아이콘 → 질문 → 반복 규칙 순서. 제목(ResponsiveDialog 헤더)은 "반복 일정 수정"
        이라는 분류만 말하고, 정작 사용자가 답해야 하는 질문은 본문 첫 줄이다.
        그래서 질문 쪽을 17px/600 으로 올리고 헤더와 문구를 겹치지 않게 나눴다.
      -->
      <div class="esd__hero">
        <span class="esd__icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M16.5 2.5 20 6l-3.5 3.5" />
            <path d="M4 12V9.5A3.5 3.5 0 0 1 7.5 6H20" />
            <path d="M7.5 21.5 4 18l3.5-3.5" />
            <path d="M20 12v2.5a3.5 3.5 0 0 1-3.5 3.5H4" />
          </svg>
        </span>
        <p class="esd__lead">{{ lead }}</p>
        <p v-if="dateLabel" class="esd__rule u-num">{{ dateLabel }}</p>
      </div>

      <div class="esd__opts" role="radiogroup" :aria-label="title">
        <button
          v-for="opt in options"
          :key="opt.value"
          type="button"
          class="esd__opt"
          :class="{ 'is-active': selected === opt.value }"
          role="radio"
          :aria-checked="selected === opt.value"
          @click="selected = opt.value"
        >
          <span class="esd__dot" aria-hidden="true" />
          <span class="esd__text">
            <span class="esd__label">{{ opt.label }}</span>
            <!-- 라벨('이 일정만')은 범위의 이름일 뿐이라 "그래서 뭐가 바뀌는데?" 에
                 답하지 못한다. 결과를 문장으로 한 줄 더 적는다. -->
            <span class="esd__desc">{{ opt.desc }}</span>
          </span>
        </button>
      </div>

      <!--
        서버(routes/events.js PUT)는 범위 수정에서 날짜를 값으로 덮지 않는다 — 이 건이
        옮겨진 **일수만큼** 범위 안 모든 건을 민다(화→수면 전부 하루). 시간은 새 시간으로
        맞춘다. 날짜가 '같은 날짜' 가 아니라 '같은 이동' 이라는 걸 적어 둔다.
      -->
      <p v-if="mode === 'edit' && selected !== 'this'" class="esd__hint">
        시간은 고른 범위의 모든 반복 건에 똑같이 적용되고, 날짜를 옮겼다면 모두 같은 일수만큼 옮겨집니다.
      </p>
    </div>

    <template #footer>
      <el-button class="esd__btn" @click="visible = false">취소</el-button>
      <el-button
        class="esd__btn"
        :type="mode === 'delete' ? 'danger' : 'primary'"
        @click="handleConfirm"
      >
        {{ mode === 'delete' ? '삭제' : '적용' }}
      </el-button>
    </template>
  </ResponsiveDialog>
</template>

<script>
import { ref, computed, watch } from 'vue'
import ResponsiveDialog from '@/components/dialogs/ResponsiveDialog.vue'

/**
 * 반복 일정의 적용 범위를 고르는 시트.
 *
 * 반복 일정은 occurrence 를 펼쳐 저장하므로(주간 회의 20건 = 20행) 범위를 묻지 않으면
 * 사용자가 20건을 하나씩 지운다. 값은 서버 계약(?scope=)과 1:1 로 맞춘다.
 *
 * 확인 단계를 겸한다 — 호출부는 이 시트 앞뒤로 ElMessageBox.confirm 을 덧붙이지 않는다.
 * 두 번 묻는 흐름은 모바일에서 특히 성가시다.
 */

/* 요일 이름. Date#toLocaleDateString('ko-KR') 은 브라우저/OS 로케일 데이터에 따라
   '일'/'일요일' 이 갈려서 캡션 길이가 튄다. 여기서 직접 찍는다. */
const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토']

/**
 * 'YYYY-MM-DD' · 'YYYY-MM-DD HH:mm:ss' · Date 를 '8월 24일 (일)' 로.
 * 값이 없거나 파싱이 안 되면 빈 문자열 — 호출부가 날짜를 안 넘겨도
 * 캡션이 'undefined 건만…' 이 되지 않아야 한다.
 */
function formatDay(value) {
  if (!value) return { day: '', full: '' }
  const ymd = value instanceof Date
    ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
    : String(value).slice(0, 10)
  const d = new Date(`${ymd}T00:00:00`)
  if (Number.isNaN(d.getTime())) return { day: '', full: '' }
  const day = `${d.getMonth() + 1}월 ${d.getDate()}일`
  return { day, full: `${day} (${WEEKDAY[d.getDay()]})` }
}

/*
 * 선택지 문구.
 *
 * 캡션은 **결과**를 적는다("8월 24일 건만 바뀌고 반복에서 분리됩니다").
 * 예전 문구는 범위를 다시 설명하기만 해서('이 날짜 하나만 삭제합니다'), 30건짜리
 * 시리즈에서 '전체' 를 골랐을 때 몇 건이 사라지는지 끝까지 알 수 없었다.
 * date 가 없으면 그 자리를 비운 일반 문장으로 떨어진다(호출부가 안 넘겨도 됨).
 */
function buildOptions(mode, day) {
  const from = day ? `${day}부터` : '이 날짜부터'
  const only = day ? `${day} 건만` : '이 건만'

  if (mode === 'edit') {
    return [
      { value: 'this', label: '이 일정만', desc: `${only} 바뀌고 반복에서 분리됩니다` },
      { value: 'thisAndFuture', label: '이 일정 및 이후 전체', desc: `${from} 이후 반복 건이 함께 바뀝니다` },
      { value: 'all', label: '전체 반복 일정', desc: '지난 건까지 포함해 모두 바뀝니다' }
    ]
  }
  return [
    { value: 'this', label: '이 일정만', desc: `${only} 삭제되고 나머지 반복은 남습니다` },
    { value: 'thisAndFuture', label: '이 일정 및 이후 전체', desc: `${from} 이후 반복 건이 함께 삭제됩니다` },
    { value: 'all', label: '전체 반복 일정', desc: '지난 건까지 포함해 모두 삭제됩니다' }
  ]
}

export default {
  name: 'EventScopeDialog',
  components: { ResponsiveDialog },
  props: {
    modelValue: { type: Boolean, default: false },
    mode: {
      type: String,
      default: 'delete',
      validator: (v) => v === 'edit' || v === 'delete'
    },
    /*
     * 지금 고른 occurrence 의 날짜. 문구를 정확하게 만들기 위한 것뿐이라 선택이다.
     * (반복 규칙 요약·전체 건수 prop 도 있었지만 넘기는 호출부가 없었다 — 수정 화면은
     *  시리즈 규칙도 건수도 받아 오지 않는다.)
     */
    date: { type: [String, Date], default: null }
  },
  emits: ['update:modelValue', 'confirm'],
  setup(props, { emit }) {
    // 가장 좁은 범위가 기본값이다. 잘못 골랐을 때 되돌릴 수 없는 쪽(전체 삭제)이
    // 실수로 눌리지 않아야 한다.
    const selected = ref('this')

    const visible = computed({
      get: () => props.modelValue,
      set: (v) => emit('update:modelValue', v)
    })

    // 시트를 다시 열면 이전 선택이 남아 있으면 안 된다 —
    // 직전에 '전체'를 골랐다는 이유로 다음 삭제가 전체가 되면 사고다.
    watch(visible, (open) => {
      if (open) selected.value = 'this'
    })

    const dayParts = computed(() => formatDay(props.date))
    const dateLabel = computed(() => dayParts.value.full)

    const options = computed(() => buildOptions(props.mode, dayParts.value.day))
    const title = computed(() => (props.mode === 'delete' ? '반복 일정 삭제' : '반복 일정 수정'))
    const lead = computed(() =>
      props.mode === 'delete'
        ? '이 반복 일정을 어디까지 삭제할까요?'
        : '이 반복 일정을 어디까지 수정할까요?'
    )

    function handleConfirm() {
      emit('confirm', selected.value)
    }

    return { visible, selected, options, title, lead, dateLabel, handleConfirm }
  }
}
</script>

<style scoped>
.esd__hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--sp-1);
  padding-bottom: var(--sp-4);
}

.esd__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-bottom: var(--sp-2);
  border-radius: var(--r-full);
  background: var(--color-primary-soft);
  color: var(--color-primary-strong);
}

/* 삭제는 되돌릴 수 없다. 헤더 문구만으로는 눈에 안 들어와서 아이콘 색으로 한 번 더 말한다. */
.esd--delete .esd__icon {
  background: var(--color-danger-soft);
  color: var(--color-danger-strong);
}

.esd__lead {
  margin: 0;
  font-size: var(--fs-lead);
  font-weight: var(--fw-semibold);
  line-height: 1.4;
  color: var(--color-text);
}

/* 반복 규칙 요약. 어떤 시리즈를 건드리는지 한 줄로 확인시킨다. */
.esd__rule {
  margin: 0;
  font-size: var(--fs-sm);
  color: var(--color-text-muted);
}

.esd__opts {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

.esd__opt {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-3);
  width: 100%;
  min-height: var(--tap-comfortable);
  padding: var(--sp-3);
  /* 1.5px. 1px 테두리로는 선택된 카드의 파란 선이 옆 카드의 회색 선과 굵기가 같아
     "골랐다" 는 신호가 색 하나에만 걸린다. */
  border: 1.5px solid var(--color-border);
  border-radius: var(--r-lg);
  background: var(--color-surface);
  /* 버튼은 UA 스타일시트가 자체 font(크롬: Arial 13.33px)를 강제한다.
     inherit 하지 않으면 이 행들만 앱의 나머지와 다른 글꼴로 렌더된다. */
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease), background var(--dur-fast) var(--ease);
}

.esd__opt:active { background: var(--color-surface-2); }

/* 선택 표시는 테두리 + 한 단계 눌린 배경까지만. primary-soft(파란 면)를 깔면
   카드 안의 캡션이 그 위에 얹혀 대비가 떨어지고, 카드 세 장 중 하나만 색면이라
   시트가 알록달록해진다. */
.esd__opt.is-active {
  border-color: var(--color-primary);
  background: var(--color-surface-3);
}

.esd--delete .esd__opt.is-active {
  border-color: var(--color-danger);
}

.esd__dot {
  width: 20px;
  height: 20px;
  margin-top: 1px;
  flex-shrink: 0;
  border: 2px solid var(--color-border-strong);
  border-radius: var(--r-full);
  background: var(--color-surface);
}

.esd__opt.is-active .esd__dot {
  border-color: var(--color-primary);
  border-width: 6px;
}

.esd--delete .esd__opt.is-active .esd__dot {
  border-color: var(--color-danger);
}

.esd__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.esd__label {
  font-size: var(--fs-md);
  font-weight: var(--fw-medium);
  color: var(--color-text);
}

.esd__desc {
  font-size: var(--fs-xs);
  color: var(--color-text-muted);
  line-height: 1.45;
}

.esd__hint {
  margin: var(--sp-3) 0 0;
  padding: var(--sp-2) var(--sp-3);
  border-radius: var(--r-md);
  background: var(--color-warning-soft);
  font-size: var(--fs-xs);
  color: var(--color-warning-strong);
  line-height: 1.5;
}

/*
 * 시트 푸터 버튼은 --tap-lg 로. ResponsiveDialog 가 깔아 둔 44px 보다 큰데,
 * 되돌리기 어려운 조작이라 오탭 여지를 더 줄인다.
 * min-height 가 아니라 height 를 쓰는 건 그 :deep 규칙과 특이도를 다투지 않으려는 것이다.
 */
@media (max-width: 767px) {
  .esd__btn {
    height: var(--tap-lg);
    font-weight: var(--fw-semibold);
  }
}
</style>
